import React, { useState, useEffect } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaPlus,
  FaTrash,
  FaEdit,
  FaSave,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  getAvailabilitySettings,
  saveAvailabilitySettings,
} from "../utils/emailUtils";
import {
  getBlockedDates,
  addBlockedDate,
  removeBlockedDate,
} from "../firebase";
import { logAdminActivity } from "../utils/adminSecurity";
import "./AvailabilityManager.css";

const AvailabilityManager = () => {
  const [availabilitySettings, setAvailabilitySettings] = useState({
    workingDays: [1, 2, 3, 4, 5], // Monday to Friday
    timeSlots: [
      "09:00",
      "09:30",
      "10:00",
      "10:30",
      "11:00",
      "11:30",
      "12:00",
      "12:30",
      "13:00",
      "13:30",
      "14:00",
      "14:30",
      "15:00",
      "15:30",
      "16:00",
      "16:30",
      "17:00",
    ],
    customTimeSlots: {}, // Format: { "2024-01-15": ["09:00", "10:00"] }
    blockedTimeSlots: {}, // Format: { "2024-01-15": ["14:00", "15:00", "16:00"] }
  });

  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState("");
  const [newBlockedReason, setNewBlockedReason] = useState("");
  const [editingDate, setEditingDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  // Date-specific time slot management
  const [selectedDateForTimeSlots, setSelectedDateForTimeSlots] = useState("");
  const [showTimeSlotManager, setShowTimeSlotManager] = useState(false);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);

  const daysOfWeek = [
    { id: 1, name: "Pazartesi", short: "Pzt" },
    { id: 2, name: "Salı", short: "Sal" },
    { id: 3, name: "Çarşamba", short: "Çar" },
    { id: 4, name: "Perşembe", short: "Per" },
    { id: 5, name: "Cuma", short: "Cum" },
    { id: 6, name: "Cumartesi", short: "Cmt" },
    { id: 0, name: "Pazar", short: "Paz" },
  ];

  const timeSlots = [
    "08:00",
    "08:30",
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
    "18:30",
    "19:00",
  ];

  useEffect(() => {
    loadAvailabilityData();
  }, []);

  const loadAvailabilityData = async () => {
    setIsLoading(true);
    try {
      const [settings, blocked] = await Promise.all([
        getAvailabilitySettings(),
        getBlockedDates(),
      ]);

      if (settings) {
        // Ensure blockedTimeSlots exists and sort timeSlots
        const sortedTimeSlots = settings.timeSlots
          ? [...settings.timeSlots].sort((a, b) => {
              const [aHours, aMinutes] = a.split(":").map(Number);
              const [bHours, bMinutes] = b.split(":").map(Number);
              const aTimeInMinutes = aHours * 60 + aMinutes;
              const bTimeInMinutes = bHours * 60 + bMinutes;
              return aTimeInMinutes - bTimeInMinutes;
            })
          : settings.timeSlots;

        setAvailabilitySettings({
          ...settings,
          timeSlots: sortedTimeSlots,
          blockedTimeSlots: settings.blockedTimeSlots || {},
        });
      }
      setBlockedDates(blocked);
    } catch (error) {
      console.error("Error loading availability data:", error);
    }
    setIsLoading(false);
  };

  const handleWorkingDayToggle = (dayId) => {
    setAvailabilitySettings((prev) => ({
      ...prev,
      workingDays: prev.workingDays.includes(dayId)
        ? prev.workingDays.filter((d) => d !== dayId)
        : [...prev.workingDays, dayId],
    }));
  };

  const handleTimeSlotToggle = (timeSlot) => {
    setAvailabilitySettings((prev) => ({
      ...prev,
      timeSlots: prev.timeSlots.includes(timeSlot)
        ? prev.timeSlots.filter((t) => t !== timeSlot)
        : [...prev.timeSlots, timeSlot],
    }));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      const previousSettings = { ...availabilitySettings };

      // Sort timeSlots before saving to ensure chronological order
      const sortedSettings = {
        ...availabilitySettings,
        timeSlots: [...availabilitySettings.timeSlots].sort((a, b) => {
          const [aHours, aMinutes] = a.split(":").map(Number);
          const [bHours, bMinutes] = b.split(":").map(Number);
          const aTimeInMinutes = aHours * 60 + aMinutes;
          const bTimeInMinutes = bHours * 60 + bMinutes;
          return aTimeInMinutes - bTimeInMinutes;
        }),
      };

      await saveAvailabilitySettings(sortedSettings);

      // Update local state with sorted settings
      setAvailabilitySettings(sortedSettings);
      alert("Ayarlar başarıyla kaydedildi!");

      // Log availability settings change
      logAdminActivity("availability_settings_change", {
        previousSettings: {
          workingDays: previousSettings.workingDays,
          timeSlots: previousSettings.timeSlots,
          customTimeSlotsCount: Object.keys(
            previousSettings.customTimeSlots || {}
          ).length,
          blockedTimeSlotsCount: Object.keys(
            previousSettings.blockedTimeSlots || {}
          ).length,
        },
        newSettings: {
          workingDays: sortedSettings.workingDays,
          timeSlots: sortedSettings.timeSlots,
          customTimeSlotsCount: Object.keys(
            sortedSettings.customTimeSlots || {}
          ).length,
          blockedTimeSlotsCount: Object.keys(
            sortedSettings.blockedTimeSlots || {}
          ).length,
        },
        timestamp: new Date().toISOString(),
        changedFields: getChangedAvailabilityFields(
          previousSettings,
          sortedSettings
        ),
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Ayarlar kaydedilirken hata oluştu.");
    }
    setIsLoading(false);
  };

  const getChangedAvailabilityFields = (previous, current) => {
    const changed = [];

    if (
      JSON.stringify(previous.workingDays) !==
      JSON.stringify(current.workingDays)
    ) {
      changed.push("workingDays");
    }
    if (
      JSON.stringify(previous.timeSlots) !== JSON.stringify(current.timeSlots)
    ) {
      changed.push("timeSlots");
    }
    if (
      JSON.stringify(previous.customTimeSlots) !==
      JSON.stringify(current.customTimeSlots)
    ) {
      changed.push("customTimeSlots");
    }
    if (
      JSON.stringify(previous.blockedTimeSlots) !==
      JSON.stringify(current.blockedTimeSlots)
    ) {
      changed.push("blockedTimeSlots");
    }

    return changed;
  };

  const handleAddBlockedDate = async () => {
    if (!newBlockedDate) return;

    setIsLoading(true);
    try {
      const dateData = {
        date: new Date(newBlockedDate),
        reason: newBlockedReason || "Uygun değil",
        createdAt: new Date(),
      };

      await addBlockedDate(dateData);
      setBlockedDates((prev) => [...prev, { ...dateData, id: Date.now() }]);
      setNewBlockedDate("");
      setNewBlockedReason("");
    } catch (error) {
      console.error("Error adding blocked date:", error);
      alert("Tarih eklenirken hata oluştu.");
    }
    setIsLoading(false);
  };

  const handleRemoveBlockedDate = async (dateId) => {
    setIsLoading(true);
    try {
      await removeBlockedDate(dateId);
      setBlockedDates((prev) => prev.filter((d) => d.id !== dateId));
    } catch (error) {
      console.error("Error removing blocked date:", error);
      alert("Tarih silinirken hata oluştu.");
    }
    setIsLoading(false);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("tr-TR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getTodayDate = () => {
    // Use Turkey time (UTC+3)
    const now = new Date();
    const turkeyTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    const year = turkeyTime.getUTCFullYear();
    const month = String(turkeyTime.getUTCMonth() + 1).padStart(2, "0");
    const day = String(turkeyTime.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateSpecificTimeSlots = (date) => {
    setSelectedDateForTimeSlots(date);
    const dateKey = date;
    const currentBlockedSlots =
      availabilitySettings?.blockedTimeSlots?.[dateKey] || [];
    setSelectedTimeSlots(currentBlockedSlots);
    setShowTimeSlotManager(true);
  };

  const handleTimeSlotToggleForDate = (timeSlot) => {
    setSelectedTimeSlots((prev) =>
      prev.includes(timeSlot)
        ? prev.filter((slot) => slot !== timeSlot)
        : [...prev, timeSlot]
    );
  };

  const handleSaveDateSpecificTimeSlots = async () => {
    const dateKey = selectedDateForTimeSlots;
    const updatedSettings = {
      ...availabilitySettings,
      blockedTimeSlots: {
        ...(availabilitySettings?.blockedTimeSlots || {}),
        [dateKey]: selectedTimeSlots,
      },
    };

    setAvailabilitySettings(updatedSettings);

    try {
      await saveAvailabilitySettings(updatedSettings);
      alert("Tarih-özel saat ayarları kaydedildi!");
      setShowTimeSlotManager(false);
    } catch (error) {
      console.error("Error saving date-specific settings:", error);
      alert("Ayarlar kaydedilirken hata oluştu.");
    }
  };

  return (
    <div className="availability-manager">
      <div className="availability-header">
        <h2>
          <FaCalendarAlt className="section-icon" />
          Uygunluk Yönetimi
        </h2>
        <p>Randevu takvimini ve uygun saatleri yönetin</p>
      </div>

      {/* Working Days Section */}
      <div className="availability-section">
        <h3>
          <FaCalendarAlt className="subsection-icon" />
          Çalışma Günleri
        </h3>
        <div className="working-days">
          {daysOfWeek.map((day) => (
            <button
              key={day.id}
              className={`day-button ${
                availabilitySettings.workingDays.includes(day.id)
                  ? "active"
                  : ""
              }`}
              onClick={() => handleWorkingDayToggle(day.id)}
            >
              <span className="day-short">{day.short}</span>
              <span className="day-name">{day.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Time Slots Section */}
      <div className="availability-section">
        <h3>
          <FaClock className="subsection-icon" />
          Uygun Saatler
        </h3>
        <div className="time-slots-grid">
          {timeSlots.map((slot) => (
            <button
              key={slot}
              className={`time-slot-button ${
                availabilitySettings.timeSlots.includes(slot) ? "active" : ""
              }`}
              onClick={() => handleTimeSlotToggle(slot)}
            >
              {slot}
            </button>
          ))}
        </div>
      </div>

      {/* Save Settings */}
      <div className="save-section">
        <button
          className="save-button"
          onClick={handleSaveSettings}
          disabled={isLoading}
        >
          <FaSave className="button-icon" />
          {isLoading ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </button>
      </div>

      {/* Date-specific Time Slot Management */}
      <div className="availability-section">
        <h3>
          <FaClock className="subsection-icon" />
          Tarih-Özel Saat Yönetimi
        </h3>
        <p>Belirli bir tarih için hangi saatlerin kapalı olacağını seçin</p>

        <div className="date-specific-controls">
          <div className="input-group">
            <label>Tarih Seçin</label>
            <input
              type="date"
              value={selectedDateForTimeSlots}
              onChange={(e) => handleDateSpecificTimeSlots(e.target.value)}
              min={getTodayDate()}
            />
          </div>

          {showTimeSlotManager && (
            <div className="date-time-slot-manager">
              <h4>
                Kapatılacak Saatler -{" "}
                {formatDate(new Date(selectedDateForTimeSlots))}
              </h4>
              <div className="time-slots-grid">
                {timeSlots.map((slot) => (
                  <button
                    key={slot}
                    className={`time-slot-button ${
                      selectedTimeSlots.includes(slot) ? "blocked" : "available"
                    }`}
                    onClick={() => handleTimeSlotToggleForDate(slot)}
                  >
                    {slot}
                    {selectedTimeSlots.includes(slot) && " ❌"}
                  </button>
                ))}
              </div>
              <div className="date-time-controls">
                <button
                  className="save-button"
                  onClick={handleSaveDateSpecificTimeSlots}
                  disabled={isLoading}
                >
                  <FaSave className="button-icon" />
                  Tarih-Özel Ayarları Kaydet
                </button>
                <button
                  className="cancel-button"
                  onClick={() => setShowTimeSlotManager(false)}
                >
                  <FaTimes className="button-icon" />
                  İptal
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Blocked Dates Section */}
      <div className="availability-section">
        <h3>
          <FaExclamationTriangle className="subsection-icon" />
          Engellenmiş Tarihler
        </h3>

        {/* Add New Blocked Date */}
        <div className="add-blocked-date">
          <div className="blocked-date-inputs">
            <div className="input-group">
              <label>Tarih</label>
              <input
                type="date"
                value={newBlockedDate}
                onChange={(e) => setNewBlockedDate(e.target.value)}
                min={getTodayDate()}
              />
            </div>
            <div className="input-group">
              <label>Sebep (Opsiyonel)</label>
              <input
                type="text"
                placeholder="Örn: Okul dönemi, tatil, önemli toplantı"
                value={newBlockedReason}
                onChange={(e) => setNewBlockedReason(e.target.value)}
              />
            </div>
            <button
              className="add-button"
              onClick={handleAddBlockedDate}
              disabled={!newBlockedDate || isLoading}
            >
              <FaPlus className="button-icon" />
              Ekle
            </button>
          </div>
        </div>

        {/* Blocked Dates List */}
        <div className="blocked-dates-list">
          {blockedDates.length === 0 ? (
            <div className="no-blocked-dates">
              <FaCheck className="no-data-icon" />
              <p>Henüz engellenmiş tarih bulunmuyor</p>
            </div>
          ) : (
            blockedDates.map((blockedDate) => (
              <div key={blockedDate.id} className="blocked-date-item">
                <div className="blocked-date-info">
                  <div className="blocked-date-main">
                    <FaCalendarAlt className="blocked-date-icon" />
                    <span className="blocked-date-text">
                      {formatDate(blockedDate.date)}
                    </span>
                  </div>
                  {blockedDate.reason && (
                    <div className="blocked-date-reason">
                      <span>{blockedDate.reason}</span>
                    </div>
                  )}
                </div>
                <button
                  className="remove-button"
                  onClick={() => handleRemoveBlockedDate(blockedDate.id)}
                  disabled={isLoading}
                >
                  <FaTrash />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityManager;
